import React from "react";
import dayjs from "dayjs";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { Flex } from "../../../components/Flex";
import { Text } from "../../../components/Text";
import { useAppSelector } from "../../../redux/store";
import { reportSliceSelectors } from "../../../redux/report/report.selector";
import { ActivityEntityKind } from "../../../model/activity/types/ActivityEntityKind.enum";
import { useTheme } from "../../../ThemeContext";
import { getKindColor } from "../../../utils/Reports";

const Frame = styled(Flex)`
  width: 100%;
  height: 14rem;
`;

type Props = {
  kind: ActivityEntityKind;
};

/**
 * Activity per day for one entity kind. This is the chart the summary tabs
 * actually needed: the tiles already give the totals, so the only thing left
 * worth drawing is the direction they are moving in.
 */
export const ReportTrendChart: React.FC<Props> = ({ kind }) => {
  const { t } = useTranslation();
  const { theme } = useTheme();

  const trend = useAppSelector(reportSliceSelectors.selectReport)?.trend || [];

  // A single day is a dot, not a trend — the range picker is the way to see
  // more, and drawing one point invites reading a line into it.
  if (trend.length < 2 || trend.every((point) => !point[kind])) {
    return null;
  }

  const color = getKindColor(kind, theme);
  const gradientId = `trend-${kind}`;

  return (
    <Flex flexDirection="column" gap="xs">
      <Text fontWeight="bold">{t("reports.trend.title")}</Text>
      <Frame>
        <ResponsiveContainer>
          <AreaChart data={trend} margin={{ top: 8, right: 8, bottom: 0 }}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.35} />
                <stop offset="100%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="day"
              tick={{ fontSize: 11 }}
              tickFormatter={(day: string) => day.slice(5)}
              minTickGap={16}
            />
            <YAxis allowDecimals={false} width={28} tick={{ fontSize: 11 }} />
            <Tooltip
              wrapperStyle={{ zIndex: 999999 }}
              // `day` is a literal "YYYY-MM-DD" key, not an instant - it must
              // never round-trip through `new Date(...)`, which the ECMAScript
              // spec parses as UTC midnight: reformatted in a browser west of
              // UTC, that reads back as the day before, so today's point
              // tooltips as yesterday.
              labelFormatter={(day) =>
                dayjs(String(day), "YYYY-MM-DD").format("DD MMM YYYY")
              }
            />
            <Area
              type="monotone"
              dataKey={kind}
              name={t("reports.trend.title")}
              stroke={color}
              strokeWidth={2}
              fill={`url(#${gradientId})`}
            />
          </AreaChart>
        </ResponsiveContainer>
      </Frame>
    </Flex>
  );
};
